import axios, {AxiosAdapter, AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import {cloneDeep, isPlainObject} from 'lodash';

const cloneResponse = (response: AxiosResponse, config: InternalAxiosRequestConfig): AxiosResponse => ({
  ...response,
  config,
  headers: cloneDeep(response.headers),
  data:
    isPlainObject(response.data) ||
    Array.isArray(response.data) ||
    response.data instanceof ArrayBuffer ||
    ArrayBuffer.isView(response.data)
      ? cloneDeep(response.data)
      : response.data,
});

class GetRequestDeduplicator {
  private readonly pending = new Map<string, Promise<AxiosResponse>>();
  private readonly references = new WeakMap<object, number>();
  private nextReference = 0;

  // Functions and non-plain objects (including cancellation signals) must match by identity.
  private serialize(value: unknown): unknown {
    if (Array.isArray(value)) return ['array', value.map((item) => this.serialize(item))];
    if (isPlainObject(value)) {
      const object = value as Record<string, unknown>;
      return [
        'object',
        Object.keys(object)
          .filter((key) => object[key] !== undefined)
          .sort()
          .map((key) => [key, this.serialize(object[key])]),
      ];
    }
    if (value !== null && (typeof value === 'object' || typeof value === 'function')) {
      if (!this.references.has(value)) this.references.set(value, ++this.nextReference);
      return ['reference', this.references.get(value)];
    }
    return [typeof value, value];
  }

  private getKey(config: InternalAxiosRequestConfig, adapter: AxiosAdapter): string {
    const uri = axios.getUri(config).split('#')[0];
    const queryIndex = uri.indexOf('?');
    const params = new URLSearchParams(queryIndex === -1 ? '' : uri.slice(queryIndex + 1));
    // Keep the order of repeated values, e.g. sort=name&sort=date.
    params.sort();
    const url = queryIndex === -1 ? uri : `${uri.slice(0, queryIndex)}?${params.toString()}`;
    const headers = Object.fromEntries(
      Object.entries(config.headers.toJSON()).map(([name, value]) => [name.toLowerCase(), value]),
    );

    return JSON.stringify(
      this.serialize({
        ...config,
        url,
        headers,
        adapter,
        baseURL: undefined,
        allowAbsoluteUrls: undefined,
        params: undefined,
        paramsSerializer: undefined,
        // Request transforms have already run; response transforms run separately for each caller.
        transformRequest: undefined,
        transformResponse: undefined,
      }),
    );
  }

  async request(config: InternalAxiosRequestConfig, adapter: AxiosAdapter): Promise<AxiosResponse> {
    // Progress callbacks and consumable response types need their own transport.
    if (
      config.method !== 'get' ||
      config.onDownloadProgress ||
      config.onUploadProgress ||
      config.responseType === 'stream' ||
      config.responseType === 'document'
    ) {
      return adapter(config);
    }

    const key = this.getKey(config, adapter);
    let request = this.pending.get(key);
    if (!request) {
      request = Promise.resolve()
        .then(() => adapter(config))
        .finally(() => this.pending.delete(key));
      this.pending.set(key, request);
    }

    try {
      return cloneResponse(await request, config);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw AxiosError.from(
          error,
          error.code,
          config,
          error.request,
          error.response ? cloneResponse(error.response, config) : undefined,
        );
      }
      throw error;
    }
  }
}

// Shared across clients so equivalent requests from legacy and generated APIs can join.
const getRequests = new GetRequestDeduplicator();
const attachedInstances = new WeakSet<AxiosInstance>();
const originalAdapters = new WeakMap<AxiosAdapter, AxiosAdapter>();

export const deduplicateGetRequests = (instance: AxiosInstance): void => {
  if (attachedInstances.has(instance)) return;
  attachedInstances.add(instance);

  instance.interceptors.request.use((config) => {
    const configuredAdapter = axios.getAdapter(config.adapter);
    // A retry can reuse a config containing our wrapper.
    const adapter = originalAdapters.get(configuredAdapter) || configuredAdapter;
    config.adapter = (requestConfig) => getRequests.request(requestConfig, adapter);
    originalAdapters.set(config.adapter, adapter);
    return config;
  });
};
